/* ChaseLadderSection.jsx — calm operational coordination view.
 *
 * Mounted inside <InterventionQueuePage />. Lists active auto-escalation
 * ladders from /api/chase-ladder/status with calm pause/resume/resolve
 * controls. Hidden when no ladders are active (calm by default).
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  PauseCircle, PlayCircle, CheckCircle2, Clock, AlertOctagon,
  ShieldAlert, Sparkles, ChevronRight, Loader2,
} from "lucide-react";
import { api } from "../../lib/api";


const STAGE_PALETTE = {
  1: { fg: "#5B21B6", bg: "rgba(139,92,246,0.10)", border: "rgba(139,92,246,0.28)", label: "Calm reminder" },
  2: { fg: "#92400E", bg: "rgba(245,158,11,0.10)", border: "rgba(245,158,11,0.30)", label: "Firm follow-up" },
  3: { fg: "#9F1239", bg: "rgba(244,63,94,0.10)", border: "rgba(244,63,94,0.30)", label: "Escalated" },
};


const PARTICIPANT_LABEL = {
  solicitor: "Solicitor",
  broker:    "Broker",
  buyer:     "Buyer",
  seller:    "Seller",
  internal:  "Internal",
};


const _fmtRelative = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(+d)) return "—";
  const diffMs = d - Date.now();
  const future = diffMs > 0;
  const abs = Math.abs(diffMs);
  const mins = Math.round(abs / 60000);
  if (mins < 60) return `${future ? "in " : ""}${mins}m${future ? "" : " ago"}`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${future ? "in " : ""}${hours}h${future ? "" : " ago"}`;
  const days = Math.round(hours / 24);
  return `${future ? "in " : ""}${days}d${future ? "" : " ago"}`;
};


const LadderRow = ({ ladder, onAction, busyAction }) => {
  const palette = STAGE_PALETTE[ladder.stage] || STAGE_PALETTE[1];
  const participant = PARTICIPANT_LABEL[ladder.participant] || "Participant";

  return (
    <li
      className="rounded-2xl bg-white px-4 py-3 flex items-center gap-3"
      style={{
        border: ladder.paused
          ? "1px dashed rgba(15,23,42,0.18)"
          : "1px solid rgba(15,23,42,0.08)",
        opacity: ladder.paused ? 0.78 : 1,
      }}
      data-testid={`chase-ladder-row-${ladder.ladder_id}`}
    >
      <span
        className="shrink-0 w-9 h-9 rounded-xl inline-flex items-center justify-center"
        style={{ background: palette.bg, color: palette.fg, border: `1px solid ${palette.border}` }}
        aria-hidden="true"
      >
        {ladder.stage === 3 ? (
          <ShieldAlert size={14} strokeWidth={2.3} />
        ) : ladder.stage === 2 ? (
          <AlertOctagon size={14} strokeWidth={2.3} />
        ) : (
          <Sparkles size={14} strokeWidth={2.3} />
        )}
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.16em]"
            style={{ background: palette.bg, color: palette.fg }}
          >
            Stage {ladder.stage} · {palette.label}
          </span>
          {ladder.paused ? (
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.16em]"
              style={{ background: "rgba(15,23,42,0.05)", color: "#475569" }}
              data-testid={`chase-ladder-paused-pill-${ladder.ladder_id}`}
            >
              Paused
            </span>
          ) : null}
          <span className="text-[10.5px] font-bold uppercase tracking-[0.14em]"
                style={{ color: "#64748B" }}>
            {participant} · {ladder.category?.replaceAll("_", " ")}
          </span>
        </div>
        <p
          className="mt-1 text-[12.5px] flex items-center gap-2 flex-wrap"
          style={{ color: "#475569" }}
        >
          <Clock size={11} strokeWidth={2.3} style={{ color: "#94A3B8" }} />
          Last: <span className="font-semibold">{_fmtRelative(ladder.last_action_at)}</span>
          {ladder.next_due_at ? (
            <>
              <span style={{ color: "#CBD5E1" }}>·</span>
              Next: <span className="font-semibold" style={{ color: palette.fg }}>{_fmtRelative(ladder.next_due_at)}</span>
            </>
          ) : (
            <>
              <span style={{ color: "#CBD5E1" }}>·</span>
              <span className="font-semibold" style={{ color: "#9F1239" }}>Awaiting operator</span>
            </>
          )}
        </p>
      </div>

      <div className="shrink-0 flex items-center gap-1.5">
        {ladder.paused ? (
          <button
            type="button"
            onClick={() => onAction(ladder, "resume")}
            disabled={busyAction === ladder.ladder_id}
            className="inline-flex items-center gap-1 px-2.5 rounded-lg text-[11.5px] font-bold transition-colors hover:bg-slate-50 min-h-[36px]"
            style={{ color: "#5B21B6", border: "1px solid rgba(139,92,246,0.28)" }}
            data-testid={`chase-ladder-resume-${ladder.ladder_id}`}
          >
            <PlayCircle size={12} />
            Resume
          </button>
        ) : (
          <button
            type="button"
            onClick={() => onAction(ladder, "pause")}
            disabled={busyAction === ladder.ladder_id}
            className="inline-flex items-center gap-1 px-2.5 rounded-lg text-[11.5px] font-semibold transition-colors hover:bg-slate-50 min-h-[36px]"
            style={{ color: "#475569", border: "1px solid rgba(15,23,42,0.08)" }}
            data-testid={`chase-ladder-pause-${ladder.ladder_id}`}
          >
            <PauseCircle size={12} />
            Pause
          </button>
        )}
        <button
          type="button"
          onClick={() => onAction(ladder, "resolve")}
          disabled={busyAction === ladder.ladder_id}
          className="inline-flex items-center gap-1 px-2.5 rounded-lg text-[11.5px] font-bold transition-all min-h-[36px]"
          style={{
            background: "linear-gradient(135deg, #059669 0%, #10B981 100%)",
            color: "#FFFFFF",
            boxShadow: "0 4px 10px -5px rgba(16,185,129,0.55)",
          }}
          data-testid={`chase-ladder-resolve-${ladder.ladder_id}`}
        >
          {busyAction === ladder.ladder_id ? (
            <Loader2 size={12} className="animate-spin" />
          ) : (
            <CheckCircle2 size={12} />
          )}
          Resolve
        </button>
      </div>
    </li>
  );
};


const ToteRow = ({ label, value, accent }) => (
  <div
    className="px-3 py-1.5 rounded-lg flex items-center gap-2"
    style={{ background: "rgba(15,23,42,0.04)", border: "1px solid rgba(15,23,42,0.06)" }}
  >
    <span className="w-1.5 h-1.5 rounded-full" style={{ background: accent }} aria-hidden />
    <span className="text-[10.5px] font-bold uppercase tracking-[0.14em]" style={{ color: "#64748B" }}>
      {label}
    </span>
    <span className="text-[12px] font-bold tabular-nums" style={{ color: "#0F172A" }}>
      {value}
    </span>
  </div>
);


export const ChaseLadderSection = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyAction, setBusyAction] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get("/api/chase-ladder/status");
      setStatus(data || null);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const handleAction = useCallback(async (ladder, action) => {
    setBusyAction(ladder.ladder_id);
    try {
      const { data } = await api.post(
        `/api/chase-ladder/${encodeURIComponent(ladder.ladder_id)}/${action}`,
        {},
      );
      if (data?.ok) {
        toast.success(
          action === "pause"   ? "Chase paused."
          : action === "resume" ? "Chase resumed."
          : "Marked resolved.",
        );
        await reload();
      } else {
        toast.error("Could not update chase ladder.");
      }
    } catch {
      toast.error("Could not update chase ladder.");
    } finally {
      setBusyAction(null);
    }
  }, [reload]);

  if (loading) {
    return (
      <section className="mb-5" data-testid="chase-ladder-section-skeleton">
        <div
          className="h-[64px] rounded-2xl"
          style={{ background: "rgba(15,23,42,0.04)", border: "1px solid rgba(15,23,42,0.05)" }}
        />
      </section>
    );
  }
  if (!status || !Array.isArray(status.ladders) || status.ladders.length === 0) {
    return null;   // Calm by default — no ladders, no section.
  }

  const totals = status.totals || {};

  return (
    <section className="mb-5" data-testid="chase-ladder-section">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2.5">
        <div className="min-w-0">
          <p
            className="text-[10.5px] font-bold uppercase tracking-[0.18em]"
            style={{ color: "#94A3B8" }}
          >
            Auto-escalating chase
          </p>
          <h2
            className="mt-0.5 text-[14px] sm:text-[15px] font-bold tracking-tight"
            style={{ color: "#0F172A" }}
          >
            {totals.total} ladder{totals.total === 1 ? "" : "s"} actively coordinated
          </h2>
        </div>
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          className="inline-flex items-center gap-1 text-[11.5px] font-bold transition-colors hover:text-violet-700"
          style={{ color: "#6D28D9" }}
          data-testid="chase-ladder-toggle"
        >
          {collapsed ? "Show details" : "Hide details"}
          <ChevronRight
            size={11}
            strokeWidth={2.5}
            className="transition-transform"
            style={{ transform: collapsed ? "rotate(90deg)" : "rotate(-90deg)" }}
          />
        </button>
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-3" data-testid="chase-ladder-totals">
        <ToteRow label="Calm"      value={totals.calm}      accent="#7C3AED" />
        <ToteRow label="Firm"      value={totals.firm}      accent="#F59E0B" />
        <ToteRow label="Escalated" value={totals.escalated} accent="#F43F5E" />
        {totals.paused > 0 ? <ToteRow label="Paused" value={totals.paused} accent="#94A3B8" /> : null}
      </div>

      {!collapsed ? (
        <ul className="space-y-2" data-testid="chase-ladder-list">
          {status.ladders.filter((l) => !l.resolved).map((l) => (
            <LadderRow
              key={l.ladder_id}
              ladder={l}
              onAction={handleAction}
              busyAction={busyAction}
            />
          ))}
        </ul>
      ) : null}
    </section>
  );
};

export default ChaseLadderSection;
